// /admin/people/:id: one person's account: subscription (with free Pro from Morsel),
// payments from Chargebee, family members, groups, recipes, details, access switches,
// cookbook orders, and closing the account.
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'

import {
  closePerson,
  endFreePro,
  exportRecipes,
  getPerson,
  getPersonBilling,
  getPersonLookalikes,
  giveFreePro,
  removeFromGroup,
  sendPasswordReset,
  setCookbookBeta,
  setFeature,
  setPersonAccess,
  setSharedEmails,
  updatePerson,
} from '../../../api/adminV2'
import { EMAIL_PATTERN } from '../../../utils/constants'
import {
  firstName,
  formatDate,
  initials,
  LoadError,
  Loading,
  money,
  moneyFromCents,
  Pill,
  RECIPE_SOURCE,
  saveFile,
  useFeedback,
  useLoad,
} from '../ui'
import { PersonLookalikes } from './SecondAccounts'

const MAX_FAMILY = 6
// What a new account gets; used when an admin switches email updates back on
const DEFAULT_NOTIFICATIONS = [
  'user_joined_group',
  'recipe_published_to_group',
  'comment_on_my_recipe',
  'reply_to_my_comment',
]
const NAME_PATTERN = /^[0-9а-яА-Яa-zA-Zі ]+$/

const EXTEND_CHOICES = [
  ['1m', '1 month'],
  ['3m', '3 months'],
  ['1y', '1 year'],
  ['date', 'Pick a date'],
]

const PLAN_NAMES = {
  'Morsel-Pro-USD-Yearly': 'Pro',
  'Morsel-Family-Pro-USD-Yearly': 'Family Pro',
}

// Chargebee's subscription, in a sentence
const subscriptionText = (sub) => {
  switch (sub.status) {
    case 'active':
      return sub.next_billing_at ? `Renews ${formatDate(sub.next_billing_at)}` : 'Active'
    case 'non_renewing':
      return `Cancelled, so it won't renew. Pro until ${formatDate(sub.term_end)}`
    case 'cancelled':
      return `Cancelled ${formatDate(sub.cancelled_at)}`
    case 'paused':
      return 'Paused in Chargebee'
    case 'in_trial':
      return `Trial until ${formatDate(sub.term_end)}`
    case 'future':
      return 'Starts later'
    default:
      return sub.status
  }
}

const PAYMENT_STATUS = {
  success: null,
  failure: ['bad', 'Failed'],
  voided: ['plain', 'Voided'],
  in_progress: ['warn', 'In progress'],
  timeout: ['bad', 'Timed out'],
  needs_attention: ['warn', 'Needs a look'],
}

// Subscription and every payment and refund, read live from Chargebee
const PaymentHistory = ({ id }) => {
  const { data, error, loading, reload } = useLoad(() => getPersonBilling(id), [id])

  let body
  if (error && !data) body = <LoadError error={error} onRetry={reload} />
  else if (loading && !data) body = <Loading />
  else if (data && !data.available)
    body = (
      <p className="a-empty">
        Chargebee couldn't be reached, so payments aren't showing right now.{' '}
        <button type="button" className="a-btn a-small" onClick={reload}>
          Try again
        </button>
      </p>
    )
  else if (data) {
    const sub = data.subscription
    const payments = data.payments || []
    body = (
      <>
        {sub ? (
          <dl className="a-kv">
            <dt>Chargebee</dt>
            <dd>
              {sub.plan_name || 'Subscription'} · {subscriptionText(sub)}
            </dd>
            <dt>Started</dt>
            <dd>{formatDate(sub.started_at)}</dd>
          </dl>
        ) : null}
        {payments.length ? (
          <div className="a-table-wrap">
            <table className="a-table">
              <tbody>
                {payments.map((t) => {
                  const flag = PAYMENT_STATUS[t.status]
                  const refund = t.type === 'refund'
                  return (
                    <tr key={t.id}>
                      <td className="a-who">
                        <b>{refund ? 'Refund' : t.description || 'Payment'}</b>
                        <span>{formatDate(t.date)}</span>
                      </td>
                      <td>{flag ? <Pill kind={flag[0]}>{flag[1]}</Pill> : null}</td>
                      <td className="a-right a-nowrap">
                        {refund ? '−' : ''}
                        {money(t.amount)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="a-empty">
            {sub ? 'No payments in Chargebee.' : 'Never subscribed through Chargebee.'}
          </p>
        )}
      </>
    )
  }

  return (
    <section className="a-panel">
      <div className="a-panel-head">
        <h2>Payments</h2>
        {data?.chargebee_url ? (
          <a className="a-btn a-small" href={data.chargebee_url} target="_blank" rel="noreferrer">
            Open in Chargebee
          </a>
        ) : null}
      </div>
      {body}
    </section>
  )
}

const orderTotal = (o) =>
  o.total_cents !== undefined && o.total_cents !== null
    ? moneyFromCents(o.total_cents)
    : money(o.total)

const ORDER_STATUS = {
  pending_payment: 'Waiting for payment',
  paid: 'Paid',
  submitted: 'Sent to Lulu',
  in_production: 'Printing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  failed: 'Needs a look',
  refunded: 'Refunded',
  cancelled: 'Cancelled',
}

// The new paid-through date for a choice, counted from the later of today and the current date
const extendedDate = (current, choice, picked) => {
  if (choice === 'date') return picked
  const base = moment.max(moment(), current ? moment(current) : moment())
  const amount = { '1m': [1, 'month'], '3m': [3, 'months'], '1y': [1, 'year'] }[choice]
  return base.clone().add(amount[0], amount[1]).format('YYYY-MM-DD')
}

const Person = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast, toastError, confirm } = useFeedback()
  const { data: p, error, loading, reload, setData } = useLoad(() => getPerson(id), [id])
  const { data: lookalikes } = useLoad(() => getPersonLookalikes(id).catch(() => []), [id])
  const [busy, setBusy] = useState('')

  if (error && !p) return <LoadError error={error} onRetry={reload} />
  if (loading && !p) return <Loading />
  if (!p) return null

  const name = p.fullname || p.email
  const first = firstName(p.fullname)
  const closed = p.status_key === 'closed'
  const isFamilyOwner = p.plan_label === 'Family Pro'
  const ownsPlan = p.plan_label === 'Pro' || p.plan_label === 'Family Pro'
  const onHold = p.status_key === 'paused' || p.status_key === 'expired'
  const freePro = p.free_pro
  const canGiveFreePro =
    !p.gifter && ['free', 'free_pro', 'lapsed', 'paused', 'expired'].includes(p.status_key)
  const family = p.family_members || []
  const groups = p.groups || []
  const recipes = p.recipes?.items || []
  const recipeTotal = Number(p.recipes?.total) || recipes.length
  const orders = p.cookbook_orders || []
  const emailOn = (p.notifications || []).length > 0
  const smsOn = !!(p.sms_account_opt_in || p.sms_marketing_opt_in)

  const patch = (changes) => setData({ ...p, ...changes })

  /* ----- actions ----- */

  const passwordReset = () =>
    confirm({
      title: 'Send a password reset?',
      text: `We'll email ${p.email} a link to set a new password.`,
      action: 'Send email',
      run: () => sendPasswordReset(p.email),
    }).then((ok) => ok && toast(`Emailed ${first} a link to set a new password`))

  const giveExtraTime = () =>
    confirm({
      title: 'Give extra time',
      text: "Moves the paid-through date later. Chargebee is updated too, so they won't be charged until the new date.",
      action: 'Save new date',
      initial: { choice: '1m', picked: '' },
      valid: (v) => v.choice !== 'date' || !!v.picked,
      render: (v, setV) => {
        const next = extendedDate(p.end_subscription, v.choice, v.picked)
        return (
          <>
            <div className="a-btn-row" role="group" aria-label="How much extra time">
              {EXTEND_CHOICES.map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  className="a-chip"
                  aria-pressed={v.choice === key}
                  onClick={() => setV({ ...v, choice: key })}
                >
                  {label}
                </button>
              ))}
            </div>
            {v.choice === 'date' ? (
              <input
                type="date"
                aria-label="New paid-through date"
                min={moment().add(1, 'day').format('YYYY-MM-DD')}
                value={v.picked}
                onChange={(e) => setV({ ...v, picked: e.target.value })}
              />
            ) : null}
            <p>
              Now paid through {formatDate(p.end_subscription)}.
              {next ? ` New date: ${formatDate(next)}.` : ''}
            </p>
          </>
        )
      },
      run: (v) => {
        const next = extendedDate(p.end_subscription, v.choice, v.picked)
        return updatePerson(p.id, { end_subscription: next }).then(() => next)
      },
    }).then((next) => {
      if (!next) return
      toast(`${first} is now paid through ${formatDate(next)}`)
      reload()
    })

  const giveFreeProAction = () =>
    confirm({
      title: freePro ? 'Change free Pro' : 'Give free Pro',
      text: freePro
        ? 'Add more time, or pick a new last day.'
        : 'They get everything in Pro, with nothing to pay and no card needed. After the last day they go back to the free plan on their own.',
      action: freePro ? 'Save new date' : 'Give free Pro',
      initial: { choice: '1m', picked: '', note: freePro?.note || '' },
      valid: (v) => v.choice !== 'date' || !!v.picked,
      render: (v, setV) => {
        const until = extendedDate(freePro ? p.end_subscription : null, v.choice, v.picked)
        return (
          <>
            <div className="a-btn-row" role="group" aria-label="How long">
              {EXTEND_CHOICES.map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  className="a-chip"
                  aria-pressed={v.choice === key}
                  onClick={() => setV({ ...v, choice: key })}
                >
                  {label}
                </button>
              ))}
            </div>
            {v.choice === 'date' ? (
              <input
                type="date"
                aria-label="Last day of free Pro"
                min={moment().add(1, 'day').format('YYYY-MM-DD')}
                value={v.picked}
                onChange={(e) => setV({ ...v, picked: e.target.value })}
              />
            ) : null}
            <input
              type="text"
              aria-label="Why (optional)"
              placeholder="Why (optional, only admins see this)"
              maxLength={200}
              value={v.note}
              onChange={(e) => setV({ ...v, note: e.target.value })}
            />
            <p>
              {freePro ? `Now free Pro through ${formatDate(p.end_subscription)}. ` : ''}
              {until ? `Pro through ${formatDate(until)}.` : ''}
            </p>
          </>
        )
      },
      run: (v) => {
        const until = extendedDate(freePro ? p.end_subscription : null, v.choice, v.picked)
        return giveFreePro(p.id, until, v.note.trim()).then(() => until)
      },
    }).then((until) => {
      if (!until) return
      toast(`${first} has free Pro through ${formatDate(until)}`)
      reload()
    })

  const endFreeProAction = () =>
    confirm({
      title: 'End free Pro now?',
      text: `${first} moves to the free plan straight away. Their recipes are kept; any past the free limit are locked until they upgrade.`,
      action: 'End free Pro',
      danger: true,
      run: () => endFreePro(p.id),
    }).then((ok) => {
      if (!ok) return
      toast(`${first} is on the free plan now`)
      reload()
    })

  const toggleAccess = () => {
    const pausing = !onHold
    return confirm(
      pausing
        ? {
            title: 'Pause access?',
            text: "They keep their account and recipes but can't add new ones until you restore access. They aren't charged while paused.",
            action: 'Pause access',
            danger: true,
            run: () => setPersonAccess(p.id, true),
          }
        : {
            title: 'Restore access?',
            text: 'They can use Morsel again. If their paid-through date has passed they go back to the free plan.',
            action: 'Restore access',
            run: () => setPersonAccess(p.id, false),
          }
    ).then((ok) => {
      if (!ok) return
      toast(pausing ? `${first}'s access is paused` : `${first}'s access is restored`)
      reload()
    })
  }

  const removeFamilyMember = (member) =>
    confirm({
      title: `Remove ${member.fullname || member.email} from Family Pro?`,
      text: `They keep their account and recipes and move to the free plan. ${first} gets the place back.`,
      action: 'Remove from plan',
      danger: true,
      run: () =>
        setSharedEmails(
          p.id,
          family.filter((m) => m.email !== member.email).map((m) => m.email)
        ),
    }).then((ok) => {
      if (!ok) return
      toast(`${member.fullname || member.email} removed from the plan`)
      reload()
    })

  const addFamilyMember = () =>
    confirm({
      title: 'Add a family member',
      text: `They get Family Pro through ${first}'s plan.`,
      action: 'Add',
      initial: '',
      valid: (v) => EMAIL_PATTERN.test((v || '').trim()),
      render: (v, setV, ok) => (
        <input
          type="email"
          aria-label="Their email address"
          placeholder="name@example.com"
          value={v}
          autoFocus
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ok()}
        />
      ),
      run: (v) => {
        const email = v.trim().toLowerCase()
        if (family.some((m) => m.email.toLowerCase() === email)) {
          return Promise.reject('That email is already on the plan.')
        }
        return setSharedEmails(p.id, [...family.map((m) => m.email), email]).then(() => email)
      },
    }).then((email) => {
      if (!email) return
      toast(`${email} added to the plan`)
      reload()
    })

  const kickFromGroup = (group) =>
    confirm({
      title: `Remove ${first} from ${group.name}?`,
      text: "They lose access to the group. Their own recipes leave the group with them; everyone else's stay.",
      action: 'Remove',
      danger: true,
      run: () => removeFromGroup(p.id, group.id),
    }).then((ok) => {
      if (!ok) return
      toast(`${first} removed from ${group.name}`)
      reload()
    })

  const editField = (field) => {
    const isName = field === 'fullname'
    return confirm({
      title: isName ? 'Edit name' : 'Edit email',
      text: isName ? '' : "They'll sign in with the new address from now on.",
      action: 'Save',
      initial: p[field] || '',
      valid: (v) => {
        const t = (v || '').trim()
        if (t === (p[field] || '')) return false
        return isName ? t.length >= 2 && NAME_PATTERN.test(t) : EMAIL_PATTERN.test(t)
      },
      render: (v, setV, ok) => (
        <>
          <input
            type={isName ? 'text' : 'email'}
            aria-label={isName ? 'Name' : 'Email'}
            value={v}
            autoFocus
            onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && ok()}
          />
          {isName ? <p className="a-small-text">Letters, numbers and spaces only.</p> : null}
        </>
      ),
      run: (v) => updatePerson(p.id, { [field]: v.trim() }).then(() => v.trim()),
    }).then((value) => {
      if (!value) return
      patch({ [field]: value })
      toast(isName ? 'Name saved' : 'Email saved')
    })
  }

  const flip = (key, fn) => {
    setBusy(key)
    return fn()
      .then(() => toast('Saved'))
      .catch((e) => {
        toastError(e)
        throw e
      })
      .finally(() => setBusy(''))
  }

  const toggleCookbook = () => {
    const next = !p.cookbook_beta
    flip('cookbook', () => setCookbookBeta(p.id, next))
      .then(() => patch({ cookbook_beta: next }))
      .catch(() => null)
  }

  const toggleFeature = (name) => {
    const on = (p.features || []).includes(name)
    flip(`feature-${name}`, () => setFeature(p.id, name, !on))
      .then(() =>
        patch({
          features: on ? p.features.filter((f) => f !== name) : [...(p.features || []), name],
        })
      )
      .catch(() => null)
  }

  const toggleEmail = () => {
    const next = emailOn ? [] : DEFAULT_NOTIFICATIONS
    flip('email', () => updatePerson(p.id, { notifications: next }))
      .then(() => patch({ notifications: next }))
      .catch(() => null)
  }

  const downloadRecipes = () => {
    setBusy('download')
    exportRecipes({ user_id: p.id })
      .then((bytes) => {
        saveFile(bytes, `${name} recipes.xlsx`)
        toast('Spreadsheet downloaded')
      })
      .catch((e) => toastError(e, 'The spreadsheet could not be made.'))
      .finally(() => setBusy(''))
  }

  const closeAccount = () =>
    confirm({
      title: 'Close this account?',
      text: "This can't be undone. Type CLOSE to confirm.",
      action: 'Close account',
      danger: true,
      typed: 'CLOSE',
      run: () => closePerson(p.id),
    }).then((ok) => {
      if (!ok) return
      toast(`${first}'s account is closed`)
      reload()
    })

  /* ----- page ----- */

  return (
    <>
      <div>
        <Link className="a-crumb" to="/admin/people">
          ← People
        </Link>
        <div className="a-head">
          <div className="a-person-head">
            <div className="a-blob" aria-hidden="true">
              {initials(p.fullname || p.email)}
            </div>
            <div style={{ minWidth: 0 }}>
              <h1>{name}</h1>
              <p className="a-sub">
                {p.email} · member since {formatDate(p.created_at)}
                {p.last_active ? ` · last active ${formatDate(p.last_active)}` : ''} · account #
                {p.id}
              </p>
            </div>
          </div>
          {!closed ? (
            <div className="a-btn-row">
              <button type="button" className="a-btn" onClick={passwordReset}>
                Send password reset
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="a-grid-side">
        <div className="a-stack">
          <PersonLookalikes personId={p.id} pairs={lookalikes} />
          <section className="a-panel">
            <div className="a-panel-head">
              <h2>Subscription</h2>
              <Pill status={p.status_key} />
            </div>
            <dl className="a-kv">
              <dt>Plan</dt>
              <dd>
                {p.gifter ? (
                  <>
                    Family Pro (shared by{' '}
                    <Link to={`/admin/people/${p.gifter.id}`}>
                      {p.gifter.fullname || 'their family'}
                    </Link>
                    )
                  </>
                ) : freePro ? (
                  'Pro (free from Morsel)'
                ) : (
                  p.plan_label || 'None'
                )}
              </dd>
              <dt>{freePro ? 'Free Pro until' : 'Paid through'}</dt>
              <dd>
                {['paying', 'free_pro', 'lapsed', 'paused', 'expired'].includes(p.status_key)
                  ? formatDate(p.end_subscription)
                  : 'n/a'}
              </dd>
            </dl>
            {p.status_key === 'lapsed' ? (
              <p className="a-muted a-small-text">
                Their Pro ended on {formatDate(p.end_subscription)} and they haven't been back
                since. Morsel moves them to the free plan within a few hours, or as soon as they
                open Morsel.
              </p>
            ) : null}
            {freePro ? (
              <p className="a-muted a-small-text">
                Free Pro from Morsel{freePro.by ? `, given by ${freePro.by}` : ''} on{' '}
                {formatDate(freePro.since)}
                {freePro.note ? ` (${freePro.note})` : ''}. Nothing is charged. They go back to the
                free plan after the last day.
              </p>
            ) : null}
            {p.pro_ended && ['free', 'expired', 'paused'].includes(p.status_key) ? (
              <p className="a-muted a-small-text">
                Was {PLAN_NAMES[p.pro_ended.plan_id] || 'Pro'}
                {p.pro_ended.free_pro ? ' (free from Morsel)' : ''} until{' '}
                {formatDate(p.pro_ended.at)}
                {p.pro_ended.reason === 'cancelled' ? ', cancelled in Chargebee' : ''}.
              </p>
            ) : null}
            {!closed && p.status_key !== 'unconfirmed' ? (
              <div className="a-btn-row">
                {ownsPlan && !p.gifter && p.status_key === 'paying' ? (
                  <button type="button" className="a-btn a-primary" onClick={giveExtraTime}>
                    Give extra time
                  </button>
                ) : null}
                {canGiveFreePro ? (
                  <button
                    type="button"
                    className={freePro ? 'a-btn' : 'a-btn a-primary'}
                    onClick={giveFreeProAction}
                  >
                    {freePro ? 'Change free Pro' : 'Give free Pro'}
                  </button>
                ) : null}
                {freePro ? (
                  <button type="button" className="a-btn" onClick={endFreeProAction}>
                    End free Pro
                  </button>
                ) : null}
                <button type="button" className="a-btn" onClick={toggleAccess}>
                  {onHold ? 'Restore access' : 'Pause access'}
                </button>
              </div>
            ) : null}
            {p.gifter ? (
              <p className="a-muted a-small-text">
                Their dates follow {p.gifter.fullname || 'the person sharing the plan'}'s
                subscription. Change it on that account.
              </p>
            ) : null}
          </section>

          <PaymentHistory id={p.id} />

          {isFamilyOwner ? (
            <section className="a-panel">
              <div className="a-panel-head">
                <h2>Family Pro members</h2>
                <span className="a-muted">
                  {family.length} of {MAX_FAMILY} places used
                </span>
              </div>
              {family.length ? (
                <div className="a-table-wrap">
                  <table className="a-table">
                    <tbody>
                      {family.map((m) => (
                        <tr key={m.email}>
                          <td className="a-who">
                            {m.user_id ? (
                              <Link to={`/admin/people/${m.user_id}`}>
                                <b>{m.fullname || m.email}</b>
                              </Link>
                            ) : (
                              <b>{m.fullname || m.email}</b>
                            )}
                            <span>{m.email}</span>
                          </td>
                          <td>
                            <Pill status={m.status === 'accepted' ? 'accepted' : 'pending'} />
                          </td>
                          <td className="a-right">
                            <button
                              type="button"
                              className="a-btn a-small"
                              onClick={() => removeFamilyMember(m)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="a-empty">Nobody is sharing this plan yet.</p>
              )}
              {family.length < MAX_FAMILY ? (
                <div>
                  <button type="button" className="a-btn a-small" onClick={addFamilyMember}>
                    Add a family member
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          <section className="a-panel">
            <div className="a-panel-head">
              <h2>Family groups</h2>
            </div>
            {groups.length ? (
              <div className="a-table-wrap">
                <table className="a-table">
                  <thead>
                    <tr>
                      <th>Group</th>
                      <th>Role</th>
                      <th>Joined</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <tr
                        className="a-click"
                        key={g.id}
                        onClick={() => navigate(`/admin/groups/${g.id}`)}
                      >
                        <td>
                          <b>{g.name}</b>
                        </td>
                        <td>{g.role_label}</td>
                        <td className="a-nowrap">{formatDate(g.joined_at)}</td>
                        <td className="a-right">
                          <button
                            type="button"
                            className="a-btn a-small"
                            onClick={(e) => {
                              e.stopPropagation()
                              kickFromGroup(g)
                            }}
                          >
                            Remove from group
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="a-empty">Not in any family group.</p>
            )}
          </section>

          <section className="a-panel">
            <div className="a-panel-head">
              <h2>
                Recipes <span className="a-muted">({recipeTotal})</span>
              </h2>
              {recipeTotal ? (
                <button
                  type="button"
                  className="a-btn a-small"
                  disabled={busy === 'download'}
                  aria-disabled={busy === 'download'}
                  onClick={downloadRecipes}
                >
                  {busy === 'download' ? 'Preparing…' : 'Download all'}
                </button>
              ) : null}
            </div>
            {recipes.length ? (
              <div className="a-table-wrap">
                <table className="a-table">
                  <tbody>
                    {recipes.map((r) => (
                      <tr
                        className="a-click"
                        key={r.id}
                        onClick={() => navigate(`/admin/recipes/${r.id}`)}
                      >
                        <td>
                          <b>{r.name || 'Untitled recipe'}</b>
                        </td>
                        <td>
                          <Pill status={r.status_key} />
                        </td>
                        <td className="a-muted a-nowrap">{RECIPE_SOURCE[r.source] || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="a-empty">No recipes yet.</p>
            )}
            {recipeTotal > recipes.length ? (
              <p className="a-muted a-small-text">
                Showing the newest {recipes.length}. Download all to see every recipe.
              </p>
            ) : null}
          </section>
        </div>

        <div className="a-stack">
          <section className="a-panel">
            <h2>Details</h2>
            <dl className="a-kv">
              <dt>Name</dt>
              <dd>
                {p.fullname || <span className="a-muted">No name</span>}{' '}
                {!closed ? (
                  <button
                    type="button"
                    className="a-btn a-small"
                    onClick={() => editField('fullname')}
                  >
                    Edit
                  </button>
                ) : null}
              </dd>
              <dt>Email</dt>
              <dd>
                {p.email}{' '}
                {!closed ? (
                  <button
                    type="button"
                    className="a-btn a-small"
                    onClick={() => editField('email')}
                  >
                    Edit
                  </button>
                ) : null}
              </dd>
              <dt>Phone</dt>
              <dd className={p.phone ? 'a-mono' : 'a-muted'}>{p.phone || 'None'}</dd>
            </dl>
          </section>

          <section className="a-panel">
            <h2>Access and messages</h2>
            <div>
              <div className="a-toggle">
                <div>
                  <b>Can order cookbooks online (beta)</b>
                  <p>
                    Lets them pay for a printed cookbook on the site. Without it they can still
                    build one and use “Request my book” (when cookbooks are open to everyone).
                  </p>
                </div>
                <button
                  type="button"
                  className="a-switch"
                  role="switch"
                  aria-checked={!!p.cookbook_beta}
                  aria-busy={busy === 'cookbook'}
                  aria-label="Can order cookbooks online (beta)"
                  onClick={toggleCookbook}
                />
              </div>
              {Object.entries(p.feature_list || {}).map(([name, label]) => (
                <div className="a-toggle" key={name}>
                  <div>
                    <b>Early feature: {name}</b>
                    <p>{label}</p>
                  </div>
                  <button
                    type="button"
                    className="a-switch"
                    role="switch"
                    aria-checked={(p.features || []).includes(name)}
                    aria-busy={busy === `feature-${name}`}
                    aria-label={`Early feature: ${name}`}
                    onClick={() => toggleFeature(name)}
                  />
                </div>
              ))}
              <div className="a-toggle">
                <div>
                  <b>Email updates</b>
                  <p>New recipes, comments and replies</p>
                </div>
                <button
                  type="button"
                  className="a-switch"
                  role="switch"
                  aria-checked={emailOn}
                  aria-busy={busy === 'email'}
                  aria-label="Email updates"
                  onClick={toggleEmail}
                />
              </div>
              <div className="a-toggle">
                <div>
                  <b>Text messages</b>
                  <p>
                    {smsOn && p.sms_consent_at
                      ? `Agreed on ${formatDate(p.sms_consent_at)} · `
                      : ''}
                    only the customer can change this
                  </p>
                </div>
                {smsOn ? (
                  <span className="a-pill a-good">Opted in</span>
                ) : (
                  <span className="a-pill a-plain">Not opted in</span>
                )}
              </div>
            </div>
          </section>

          <section className="a-panel">
            <h2>Cookbook orders</h2>
            {orders.length ? (
              <div className="a-table-wrap">
                <table className="a-table">
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id}>
                        <td className="a-who">
                          <b>{o.title || `Order #${o.id}`}</b>
                          <span>
                            {ORDER_STATUS[o.status] || o.status} · {orderTotal(o)} ·{' '}
                            {formatDate(o.created_at)}
                          </span>
                        </td>
                        <td className="a-right">
                          <Link
                            className="a-btn a-small"
                            to={`/admin/cookbook-orders?order=${o.id}`}
                          >
                            Open
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="a-empty">No cookbook orders.</p>
            )}
          </section>

          {!closed ? (
            <section className="a-panel a-danger-zone">
              <h2>Close account</h2>
              <p className="a-muted">
                Cancels their subscription and signs them out. Their recipes stay in family groups
                so relatives keep them, and group ownership passes to the longest-standing member.
              </p>
              <div>
                <button type="button" className="a-btn a-danger" onClick={closeAccount}>
                  Close {first}'s account
                </button>
              </div>
            </section>
          ) : (
            <section className="a-panel">
              <h2>Account closed</h2>
              <p className="a-muted">This account is closed. Nothing more can be changed here.</p>
            </section>
          )}
        </div>
      </div>
    </>
  )
}

export default Person
